/**
 * Work / rest interval timer for Live Training timer exercises.
 * Pure state transitions; `now` is epoch ms so the coach clock drives players.
 *
 * Phases: idle → work → (rest → work)* → done. No rest after the last round.
 */

export const INTERVAL_PHASES = Object.freeze({
  IDLE: "idle",
  WORK: "work",
  REST: "rest",
  DONE: "done",
});

function clampConfig(config = {}) {
  const workSec = Math.min(3600, Math.max(1, Math.round(Number(config.workSec) || 30)));
  const restSec = Math.min(3600, Math.max(0, Math.round(Number(config.restSec) || 0)));
  const rounds = Math.min(99, Math.max(1, Math.round(Number(config.rounds) || 1)));
  return { workSec, restSec, rounds };
}

export function createIntervalState(config) {
  const c = clampConfig(config);
  return {
    ...c,
    phase: INTERVAL_PHASES.IDLE,
    round: 0,
    running: false,
    phaseEndsAt: null,
    remainingSec: c.workSec,
  };
}

export function configureInterval(state, config) {
  if (state?.phase && state.phase !== INTERVAL_PHASES.IDLE && state.phase !== INTERVAL_PHASES.DONE) {
    return state;
  }
  return createIntervalState({ ...state, ...config });
}

export function startInterval(state, now) {
  const base = createIntervalState(state);
  return {
    ...base,
    phase: INTERVAL_PHASES.WORK,
    round: 1,
    running: true,
    phaseEndsAt: now + base.workSec * 1000,
    remainingSec: base.workSec,
  };
}

export function intervalRemainingSec(state, now) {
  if (!state) return 0;
  if (state.running && state.phaseEndsAt) {
    return Math.max(0, Math.ceil((state.phaseEndsAt - now) / 1000));
  }
  return Math.max(0, Math.round(Number(state.remainingSec) || 0));
}

export function pauseInterval(state, now) {
  if (!state?.running) return state;
  return {
    ...state,
    running: false,
    remainingSec: intervalRemainingSec(state, now),
    phaseEndsAt: null,
  };
}

export function resumeInterval(state, now) {
  if (!state || state.running) return state;
  if (state.phase !== INTERVAL_PHASES.WORK && state.phase !== INTERVAL_PHASES.REST) {
    return state;
  }
  const remaining = Math.max(1, Math.round(Number(state.remainingSec) || 1));
  return { ...state, running: true, phaseEndsAt: now + remaining * 1000 };
}

/**
 * Move to the next phase. `startAt` is when the next phase begins (the previous
 * phase end when ticking, `now` when the coach skips).
 */
export function advanceInterval(state, startAt) {
  if (!state) return state;
  const keepRunning = state.running;
  const enter = (phase, round, durationSec) => ({
    ...state,
    phase,
    round,
    remainingSec: durationSec,
    phaseEndsAt: keepRunning ? startAt + durationSec * 1000 : null,
  });
  if (state.phase === INTERVAL_PHASES.WORK) {
    if (state.round >= state.rounds) {
      return {
        ...state,
        phase: INTERVAL_PHASES.DONE,
        running: false,
        phaseEndsAt: null,
        remainingSec: 0,
      };
    }
    if (state.restSec > 0) return enter(INTERVAL_PHASES.REST, state.round, state.restSec);
    return enter(INTERVAL_PHASES.WORK, state.round + 1, state.workSec);
  }
  if (state.phase === INTERVAL_PHASES.REST) {
    return enter(INTERVAL_PHASES.WORK, state.round + 1, state.workSec);
  }
  return state;
}

/** Apply every phase boundary passed since the last tick. */
export function tickInterval(state, now) {
  let next = state;
  let guard = 0;
  while (next?.running && next.phaseEndsAt && now >= next.phaseEndsAt && guard < 400) {
    next = advanceInterval(next, next.phaseEndsAt);
    guard += 1;
  }
  return next;
}

export function resetInterval(state) {
  return createIntervalState(state);
}

export function formatIntervalClock(totalSec) {
  const s = Math.max(0, Math.floor(Number(totalSec) || 0));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
}

/** Wire payload for `liveModeSync` (remaining time is relative, so device clocks may differ). */
export function intervalSyncState(state, now) {
  return {
    phase: state.phase,
    workSec: state.workSec,
    restSec: state.restSec,
    rounds: state.rounds,
    round: state.round,
    running: state.running,
    phaseEndsAt: state.phaseEndsAt,
    remainingSec: intervalRemainingSec(state, now),
  };
}

/** Rebuild a local running state from a received sync payload. */
export function intervalStateFromSync(state, receivedAt, ageMs = 0) {
  if (!state) return null;
  const remaining = Math.max(0, Number(state.remainingSec) || 0) - Math.max(0, ageMs) / 1000;
  return {
    ...createIntervalState(state),
    phase: state.phase,
    round: state.round,
    running: Boolean(state.running),
    remainingSec: Math.max(0, Math.round(remaining)),
    phaseEndsAt: state.running ? receivedAt + Math.max(0, remaining) * 1000 : null,
  };
}
